import React, { useEffect, useState } from 'react';
import { Save, Megaphone, AlertCircle, CheckCircle2, Upload, Image as ImageIcon, FileText, Code } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const buildUrl = (u) => {
  if (!u) return '';
  if (u.startsWith('http')) return u;
  return `${API}${u}`;
};

const Field = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <label className="block text-sm font-medium text-gray-700">{label}</label>
    {children}
    {hint && <p className="text-xs text-gray-500">{hint}</p>}
  </div>
);

const PromoTab = () => {
  const [form, setForm] = useState({
    promoEnabled: false,
    promoDisplayMode: 'both',
    promoType: 'image',
    promoTitle: '',
    promoBannerText: '',
    promoImageUrl: '',
    promoPdfUrl: '',
    promoHtml: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/site-settings`)
      .then((r) => r.json())
      .then((data) => {
        setForm({
          promoEnabled: !!data.promoEnabled,
          promoDisplayMode: data.promoDisplayMode || 'both',
          promoType: data.promoType || 'image',
          promoTitle: data.promoTitle || '',
          promoBannerText: data.promoBannerText || '',
          promoImageUrl: data.promoImageUrl || '',
          promoPdfUrl: data.promoPdfUrl || '',
          promoHtml: data.promoHtml || '',
        });
      })
      .catch(() => setStatus({ type: 'error', msg: 'Impossible de charger les paramètres.' }))
      .finally(() => setLoading(false));
  }, []);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleHtmlFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setForm((f) => ({ ...f, promoHtml: reader.result }));
      setStatus({ type: 'success', msg: 'Fichier HTML chargé. N\'oubliez pas d\'enregistrer.' });
    };
    reader.readAsText(file);
  };

  const handleUpload = async (e, targetKey) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setStatus(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/api/promo/upload`, { method: 'POST', body: fd });
      if (res.ok) {
        const data = await res.json();
        setForm((f) => ({ ...f, [targetKey]: data.url }));
        setStatus({ type: 'success', msg: 'Fichier importé. N\'oubliez pas d\'enregistrer.' });
      } else {
        setStatus({ type: 'error', msg: 'Échec de l\'import du fichier.' });
      }
    } catch (_) {
      setStatus({ type: 'error', msg: 'Erreur réseau lors de l\'import.' });
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setStatus(null);
    try {
      const res = await fetch(`${API}/api/site-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setStatus({ type: 'success', msg: 'Promotion enregistrée avec succès.' });
      } else {
        setStatus({ type: 'error', msg: 'Erreur lors de la sauvegarde.' });
      }
    } catch (_) {
      setStatus({ type: 'error', msg: 'Erreur réseau, réessayez.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-gray-500">Chargement des paramètres…</div>;

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Promotion</h2>
          <p className="text-sm text-gray-500 mt-1">Gérez la publicité mise en avant sur le site (image, PDF ou HTML).</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium disabled:opacity-50"
          data-testid="promo-save-btn"
        >
          <Save className="w-4 h-4" /> {saving ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </div>

      {status && (
        <div
          className={`flex items-center gap-2 px-4 py-3 rounded-lg mb-6 ${
            status.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}
          data-testid="promo-status"
        >
          {status.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          <span className="text-sm">{status.msg}</span>
        </div>
      )}

      {/* Activation + affichage */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center gap-2 mb-5">
          <Megaphone className="w-5 h-5 text-orange-500" />
          <h3 className="text-lg font-semibold text-gray-900">Activation</h3>
        </div>

        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg mb-5">
          <div>
            <p className="font-medium text-gray-900">Promotion active</p>
            <p className="text-xs text-gray-500">Affiche la promo sur le site public.</p>
          </div>
          <button
            type="button"
            onClick={() => setForm((f) => ({ ...f, promoEnabled: !f.promoEnabled }))}
            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${form.promoEnabled ? 'bg-orange-500' : 'bg-gray-300'}`}
            data-testid="promo-enabled-toggle"
            aria-pressed={form.promoEnabled}
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${form.promoEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="Mode d'affichage" hint="Où afficher la promo.">
            <select
              value={form.promoDisplayMode}
              onChange={update('promoDisplayMode')}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none text-sm"
              data-testid="promo-display-mode"
            >
              <option value="both">Bandeau + Pop-up</option>
              <option value="popup">Pop-up uniquement</option>
              <option value="banner">Bandeau uniquement</option>
            </select>
          </Field>
          <Field label="Type de contenu" hint="Format de la publicité.">
            <select
              value={form.promoType}
              onChange={update('promoType')}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none text-sm"
              data-testid="promo-type"
            >
              <option value="image">Image (JPEG / PNG)</option>
              <option value="pdf">PDF</option>
              <option value="html">HTML</option>
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-5">
          <Field label="Titre (optionnel)" hint="Affiché en haut du pop-up.">
            <input
              type="text"
              value={form.promoTitle}
              onChange={update('promoTitle')}
              placeholder="Ex : Offre spéciale été 2026"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none text-sm"
              data-testid="promo-title"
            />
          </Field>
          <Field label="Texte du bandeau" hint="Message court affiché dans le bandeau fin.">
            <input
              type="text"
              value={form.promoBannerText}
              onChange={update('promoBannerText')}
              placeholder="Ex : 🎉 -10% sur toute réservation avant le 30 juin !"
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none text-sm"
              data-testid="promo-banner-text"
            />
          </Field>
        </div>
      </div>

      {/* Contenu */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-5">
          {form.promoType === 'image' && <ImageIcon className="w-5 h-5 text-orange-500" />}
          {form.promoType === 'pdf' && <FileText className="w-5 h-5 text-orange-500" />}
          {form.promoType === 'html' && <Code className="w-5 h-5 text-orange-500" />}
          <h3 className="text-lg font-semibold text-gray-900">Contenu de la promotion</h3>
        </div>

        {form.promoType === 'image' && (
          <div className="space-y-4">
            <label className="flex items-center gap-2 px-4 py-2.5 bg-orange-50 text-orange-700 rounded-lg cursor-pointer hover:bg-orange-100 transition-colors w-fit text-sm font-medium" data-testid="promo-image-upload-label">
              <Upload className="w-4 h-4" /> {uploading ? 'Import en cours…' : 'Importer une image'}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUpload(e, 'promoImageUrl')} data-testid="promo-image-input" />
            </label>
            {form.promoImageUrl && (
              <img src={buildUrl(form.promoImageUrl)} alt="Aperçu" className="max-w-sm w-full rounded-lg border border-gray-200" data-testid="promo-image-preview" />
            )}
          </div>
        )}

        {form.promoType === 'pdf' && (
          <div className="space-y-4">
            <label className="flex items-center gap-2 px-4 py-2.5 bg-orange-50 text-orange-700 rounded-lg cursor-pointer hover:bg-orange-100 transition-colors w-fit text-sm font-medium" data-testid="promo-pdf-upload-label">
              <Upload className="w-4 h-4" /> {uploading ? 'Import en cours…' : 'Importer un PDF'}
              <input type="file" accept="application/pdf" className="hidden" onChange={(e) => handleUpload(e, 'promoPdfUrl')} data-testid="promo-pdf-input" />
            </label>
            {form.promoPdfUrl && (
              <a href={buildUrl(form.promoPdfUrl)} target="_blank" rel="noopener noreferrer" className="text-sm text-orange-600 underline break-all" data-testid="promo-pdf-link">
                {buildUrl(form.promoPdfUrl)}
              </a>
            )}
          </div>
        )}

        {form.promoType === 'html' && (
          <div className="space-y-4">
            <label className="flex items-center gap-2 px-4 py-2.5 bg-orange-50 text-orange-700 rounded-lg cursor-pointer hover:bg-orange-100 transition-colors w-fit text-sm font-medium" data-testid="promo-html-upload-label">
              <Upload className="w-4 h-4" /> Importer un fichier .html
              <input type="file" accept=".html,text/html" className="hidden" onChange={handleHtmlFile} data-testid="promo-html-file-input" />
            </label>
            <Field label="Code HTML" hint="Collez votre code HTML, ou importez un fichier .html ci-dessus. Le contenu s'affiche dans un cadre isolé (plein écran) pour ne pas perturber le site.">
              <textarea
                rows={12}
                value={form.promoHtml}
                onChange={update('promoHtml')}
                placeholder="<!DOCTYPE html>..."
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:outline-none text-sm font-mono"
                data-testid="promo-html-input"
              />
            </Field>
          </div>
        )}
      </div>
    </>
  );
};

export default PromoTab;
